/**
 * How far Vello may act ALONE, per intent (D639, doc 25 section 10 rule 2 and section 11 "Autonomy per intent and kill switches") -
 * governed key `central.autonomy`.
 *
 * Every Vello intent starts in SHADOW: Vello drafts the reply and a person sends it. An intent is promoted only through this setting,
 * under four eyes, after its simulation suite passes (the api refuses to promote an intent that has no passing suite). The value:
 *
 *   `{ "enabled": true, "defaultLevel": "shadow", "intents": { "<intent>": "auto" }, "disabledChannels": ["<channel>"], "disabledIntents": ["<intent>"] }`
 *
 *  - `enabled: false` is the master kill switch: Vello does not run at all (no draft, no reply) - people work the queue;
 *  - a LEVEL is one of {@link AUTONOMY_LEVELS}: `shadow` (a person sends Vello's draft), `confirm` (acts after the customer confirms),
 *    `capped` (acts alone up to the country money caps, a person approves above them) and `auto` (acts alone). The read-only intents of
 *    phase 4 need no confirmation, so for them anything above `shadow` means Vello answers by itself; the write levels are honoured by
 *    the action phases;
 *  - `defaultLevel` is what an intent with no entry gets (`shadow` unless an owner decides otherwise);
 *  - `disabledChannels` and `disabledIntents` are the kill switches: on such a channel or for such an intent Vello never goes above
 *    `shadow`, whatever the level says.
 *
 * A settings-kernel key (D393): platform and country scope, OVERRIDABLE (the country's explicit intent level wins over the platform's;
 * its `defaultLevel` over the platform's), four eyes on every change. What a lower scope can NEVER do: lift a kill switch or re-enable
 * Vello - the disabled lists UNITE across scopes and `enabled` needs both to be true. The registry default is fully conservative
 * (`enabled`, everything in shadow, no kill list), so publishing nothing changes nothing. A version the reader cannot parse fails
 * CLOSED: Vello is off (`CENTRAL_AUTONOMY_INVALID_DISABLED`) - an unreadable safety control is never read as permission.
 * Neutral: no country, channel vocabulary or intent catalogue is named here (the api checks the tokens against them).
 */
import { COUNTRY_RE, effectiveBucket, isObj, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const CENTRAL_AUTONOMY_KEY = 'central.autonomy';
export const AUTONOMY_LEVELS = ['shadow', 'confirm', 'capped', 'auto'];
/** Everything conservative: Vello on, every intent in shadow, nothing disabled. The registry default and what "nothing published" means. */
export const DEFAULT_CENTRAL_AUTONOMY = { enabled: true, defaultLevel: 'shadow', intents: {}, disabledChannels: [], disabledIntents: [] };
export const CENTRAL_AUTONOMY_REASON_CODES = ['CENTRAL_AUTONOMY_GOVERNED', 'CENTRAL_AUTONOMY_DEFAULT', 'CENTRAL_AUTONOMY_INVALID_DISABLED'];
export const CENTRAL_AUTONOMY_BOUNDS = { maxIntents: 60, maxDisabledChannels: 8, maxDisabledIntents: 60 };
/** An intent id: lower-case snake_case, 2 to 40 characters. */
export const INTENT_TOKEN_RE = /^[a-z][a-z0-9_]{1,39}$/;
/** A channel token: lower-case snake_case, 2 to 24 characters. */
export const CHANNEL_TOKEN_RE = /^[a-z][a-z0-9_]{1,23}$/;
export const isAutonomyLevel = (v) => typeof v === 'string' && AUTONOMY_LEVELS.includes(v);
const distinctTokens = (v, re, max) => Array.isArray(v) && v.length <= max && v.every((t) => typeof t === 'string' && re.test(t)) && new Set(v).size === v.length;
/** The stable issue of a value that is not an autonomy value, or undefined when it is one. */
export function centralAutonomyIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    const members = ['enabled', 'defaultLevel', 'intents', 'disabledChannels', 'disabledIntents'];
    if (!onlyMembers(v, members) || !members.every((m) => m in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    if (!isAutonomyLevel(v.defaultLevel))
        return 'INVALID_DEFAULT_LEVEL';
    if (!isObj(v.intents))
        return 'INTENTS_NOT_AN_OBJECT';
    const entries = Object.entries(v.intents);
    if (entries.length > CENTRAL_AUTONOMY_BOUNDS.maxIntents)
        return 'TOO_MANY_INTENTS';
    for (const [intent, level] of entries) {
        if (!INTENT_TOKEN_RE.test(intent))
            return 'INVALID_INTENT_ID';
        if (!isAutonomyLevel(level))
            return 'INVALID_INTENT_LEVEL';
    }
    if (!distinctTokens(v.disabledChannels, CHANNEL_TOKEN_RE, CENTRAL_AUTONOMY_BOUNDS.maxDisabledChannels))
        return 'INVALID_DISABLED_CHANNELS';
    if (!distinctTokens(v.disabledIntents, INTENT_TOKEN_RE, CENTRAL_AUTONOMY_BOUNDS.maxDisabledIntents))
        return 'INVALID_DISABLED_INTENTS';
    return undefined;
}
export function validCentralAutonomyValue(v) {
    return centralAutonomyIssue(v) === undefined;
}
/** The value normalised: intents and the disabled lists sorted, so equal meaning is equal content (and one content hash). */
function normalise(v) {
    return {
        enabled: v.enabled,
        defaultLevel: v.defaultLevel,
        intents: Object.fromEntries(Object.keys(v.intents).sort().map((k) => [k, v.intents[k]])),
        disabledChannels: [...v.disabledChannels].sort(),
        disabledIntents: [...v.disabledIntents].sort(),
    };
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function centralAutonomySetting(input) {
    const issue = centralAutonomyIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid Central autonomy: ${issue}`);
    return scopedSetting({ key: CENTRAL_AUTONOMY_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: normalise(input.value), version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The autonomy in force for `country` at `at`, over the GOVERNED reader's versions. Both buckets are read; either one unreadable
 * disables Vello (fail closed); `enabled` is the AND of the buckets that exist; the disabled lists are the UNION.
 */
export function resolveCentralAutonomy(settings, country, at) {
    const closed = (suffix) => ({ enabled: false, reasonCode: 'CENTRAL_AUTONOMY_INVALID_DISABLED', policyVersion: `${CENTRAL_AUTONOMY_KEY}@${suffix}`, buckets: [], disabledChannels: [], disabledIntents: [] });
    if (!COUNTRY_RE.test(country))
        return closed('invalid-disabled');
    const found = [];
    for (const b of [effectiveBucket(settings, CENTRAL_AUTONOMY_KEY, 'country', country, at), effectiveBucket(settings, CENTRAL_AUTONOMY_KEY, 'platform', undefined, at)]) {
        if (b === undefined)
            continue;
        if (!validCentralAutonomyValue(b.value))
            return closed('invalid-disabled');
        found.push(b);
    }
    if (found.length === 0) {
        return { enabled: DEFAULT_CENTRAL_AUTONOMY.enabled, reasonCode: 'CENTRAL_AUTONOMY_DEFAULT', policyVersion: `${CENTRAL_AUTONOMY_KEY}@default`, buckets: [], disabledChannels: [], disabledIntents: [] };
    }
    const values = found.map((b) => normalise(b.value));
    return {
        enabled: values.every((v) => v.enabled),
        reasonCode: 'CENTRAL_AUTONOMY_GOVERNED',
        policyVersion: found.map((b) => b.policyVersion).join(';'),
        buckets: values,
        disabledChannels: [...new Set(values.flatMap((v) => v.disabledChannels))].sort(),
        disabledIntents: [...new Set(values.flatMap((v) => v.disabledIntents))].sort(),
    };
}
/**
 * The level for one intent on one channel: the country's explicit entry, else the platform's, else the country's `defaultLevel`, else the
 * platform's, else `shadow`. A disabled channel or intent caps it at `shadow`. With `explicitOnly` the `defaultLevel`s are ignored: only an entry
 * naming the intent can lift it above `shadow` (the step-up write intents).
 */
export function autonomyLevelFor(decision, target) {
    const [first, second] = decision.buckets; // country first, then platform (either may be absent: then the one present is `first`)
    // `explicitOnly` (D641 fix): an intent that spends money or cancels a ride (a step-up write) is never lifted by a bucket's `defaultLevel` - only an
    // entry that NAMES it can promote it, so a default raised for the read-only intents cannot turn the write intents on.
    const configured = first?.intents[target.intent] ?? second?.intents[target.intent] ?? (target.explicitOnly === true ? undefined : (first?.defaultLevel ?? second?.defaultLevel)) ?? DEFAULT_CENTRAL_AUTONOMY.defaultLevel;
    const killedBy = decision.disabledIntents.includes(target.intent) ? 'intent' : target.channel !== undefined && decision.disabledChannels.includes(target.channel) ? 'channel' : undefined;
    return { configured, effective: killedBy !== undefined ? 'shadow' : configured, ...(killedBy !== undefined ? { killedBy } : {}) };
}
//# sourceMappingURL=central-autonomy-policy.js.map